import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, Store } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const shop = searchParams.get("shop");
  const shopifyConnected = searchParams.get("shopify_connected");
  const shopifyError = searchParams.get("shopify_error");

  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [shopDomain, setShopDomain] = useState("");
  const [showShopifyConnect, setShowShopifyConnect] = useState(false);

  useEffect(() => {
    if (shopifyError) {
      toast.error(`Shopify connection failed: ${shopifyError}`);
      setMode("register");
      return;
    }
    if (shop) {
      setMode("loading");
      axios.get(`${API}/auth/check-account?shop=${shop}`)
        .then(res => setMode(res.data.action))
        .catch(() => setMode("register"));
    } else {
      setMode("login");
    }
  }, [shop, shopifyError]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password, shop: shop || undefined });
      if (res.data.success) {
        localStorage.setItem("ff_authenticated", "true");
        localStorage.setItem("ff_user", res.data.user.email);
        localStorage.setItem("ff_access_token", res.data.access_token);
        login(true);
        navigate("/orders");
      } else {
        toast.error(res.data.message || "Invalid credentials");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email, password, name, company_name: companyName, shop_domain: shop || "",
      });
      if (res.data.success) {
        localStorage.setItem("ff_authenticated", "true");
        localStorage.setItem("ff_user", res.data.user.email);
        if (res.data.access_token) localStorage.setItem("ff_access_token", res.data.access_token);
        toast.success("Account created!");
        login(true);
        navigate("/orders");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  const handleShopifyConnect = (e) => {
    e.preventDefault();
    let s = shopDomain.trim().toLowerCase();
    if (!s) return;
    if (!s.includes(".myshopify.com")) s = s.replace(/\.myshopify\.com$/, "") + ".myshopify.com";
    window.location.href = `${API}/shopify/install?shop=${encodeURIComponent(s)}`;
  };

  if (mode === "loading") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          Connecting to Shopify...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/easywaybill-icon.webp" alt="EasyWaybill" className="w-16 h-16 mx-auto mb-4 rounded-xl" />
          <h1 className="text-slate-900 text-2xl font-bold">
            {mode === "register" ? "EasyWaybill" : "EasyWaybill"}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {mode === "register" ? "Create your account to get started" : "Sign in to manage your waybills"}
          </p>
        </div>

        {shop && shopifyConnected && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6 text-center">
            <p className="text-green-700 text-sm flex items-center justify-center gap-2">
              <Store className="w-4 h-4" />
              Shopify store <span className="font-medium">{shop}</span> connected
            </p>
          </div>
        )}

        <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-lg">
          {/* Toggle */}
          <div className="flex mb-6 bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setMode("login")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === "login" ? "bg-green-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="auth-tab-login"
            >
              Sign In
            </button>
            <button
              onClick={() => setMode("register")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === "register" ? "bg-green-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="auth-tab-register"
            >
              Create Account
            </button>
          </div>

          {mode === "login" ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-slate-700 text-sm font-medium mb-1 block">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder:text-slate-400"
                  placeholder="your-email@company.co.za" required data-testid="auth-email" />
              </div>
              <div>
                <label className="text-slate-700 text-sm font-medium mb-1 block">Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder:text-slate-400"
                  placeholder="Enter your password" required data-testid="auth-password" />
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white py-2.5 rounded-lg font-medium text-sm transition-all disabled:opacity-50"
                data-testid="auth-submit">
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="text-slate-700 text-sm font-medium mb-1 block">Company Name</label>
                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder:text-slate-400"
                  placeholder="Your Company" data-testid="auth-company" />
              </div>
              <div>
                <label className="text-slate-700 text-sm font-medium mb-1 block">Your Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder:text-slate-400"
                  placeholder="John Smith" data-testid="auth-name" />
              </div>
              <div>
                <label className="text-slate-700 text-sm font-medium mb-1 block">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder:text-slate-400"
                  placeholder="your-email@company.co.za" required data-testid="auth-email-register" />
              </div>
              <div>
                <label className="text-slate-700 text-sm font-medium mb-1 block">Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder:text-slate-400"
                  placeholder="Min 6 characters" required data-testid="auth-password-register" />
              </div>
              {shop && (
                <p className="text-xs text-slate-500">
                  Your account will be linked to: <span className="text-slate-700 font-medium">{shop}</span>
                </p>
              )}
              <button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white py-2.5 rounded-lg font-medium text-sm transition-all disabled:opacity-50"
                data-testid="auth-register-submit">
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>
          )}

          {/* Shopify Connect section */}
          {!shop && (
            <>
              <div className="relative mt-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-3 text-slate-400">or</span>
                </div>
              </div>

              {!showShopifyConnect ? (
                <a
                  href="https://apps.shopify.com/easywaybill"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 w-full bg-gradient-to-r from-lime-500 to-green-500 hover:from-lime-600 hover:to-green-600 text-white py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2"
                  data-testid="shopify-connect-btn">
                  <Store className="w-4 h-4" />
                  Sign Up with Shopify
                </a>
              ) : (
                <form onSubmit={handleShopifyConnect} className="mt-6 space-y-3">
                  <label className="text-slate-700 text-xs font-medium mb-1 block">Your Shopify store URL</label>
                  <div className="flex gap-2">
                    <input type="text" value={shopDomain} onChange={(e) => setShopDomain(e.target.value)}
                      placeholder="your-store"
                      className="flex-1 bg-white border border-slate-300 rounded-lg px-3 py-2 text-slate-900 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder:text-slate-400"
                      required autoFocus data-testid="shopify-domain-input" />
                    <span className="flex items-center text-slate-400 text-xs">.myshopify.com</span>
                  </div>
                  <button type="submit"
                    className="w-full bg-gradient-to-r from-lime-500 to-green-500 hover:from-lime-600 hover:to-green-600 text-white py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2"
                    data-testid="shopify-connect-submit">
                    <Store className="w-4 h-4" />
                    Connect Shopify Store
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        <p className="text-center text-slate-400 text-xs mt-6">
          EasyWaybill &copy; {new Date().getFullYear()}
        </p>
        <div className="text-center mt-3">
          <span className="text-slate-400 text-xs">Developed by </span>
          <a href="https://supplywatch.app/agency" target="_blank" rel="noopener noreferrer"
            className="text-purple-400 hover:text-purple-300 text-xs font-medium">
            Starflight Agency <span className="text-[10px]">&nearr;</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
